import {
  channelIntegrations,
  ioredisChannelIntegration,
  redisChannelIntegration,
  detectOrchestrionSetup,
} from '@sentry/server-utils/orchestrion';
import type { RegisterDiagnosticsChannelInjectionOptions } from '@sentry/server-utils/orchestrion/register';
import { registerDiagnosticsChannelInjection } from '@sentry/server-utils/orchestrion/register';
import { cacheResponseHook } from '../integrations/tracing/redis/cache';
import type { DiagnosticsChannelInjection } from './diagnosticsChannelInjection';
import { setDiagnosticsChannelInjectionLoader } from './diagnosticsChannelInjection';

export function diagnosticsChannelInjectionIntegrations(): typeof channelIntegrations {
  return channelIntegrations;
}

/**
 * EXPERIMENTAL: opt into diagnostics-channel-based auto-instrumentation.
 *
 * Call this BEFORE `Sentry.init()`:
 *
 * ```ts
 * import * as Sentry from '@sentry/node';
 *
 * Sentry.experimentalUseDiagnosticsChannelInjection();
 * Sentry.init({
 *   dsn: '__DSN__',
 *   // other settings...
 * });
 * ```
 *
 * When this has been called AND span recording is enabled, `Sentry.init()`
 * uses the diagnostics-channel-injection-based integrations instead of the
 * OpenTelemetry ones, and installs the module hooks that inject the channels
 * (so libraries imported after `init()` publish the channel events).
 *
 * This is a standalone function rather than an `init()` option so that a
 * bundler drops all of it (and its transitive deps) when this function isn't
 * called. `init()` reads the loader registered below.
 *
 * An app that DOES call it gets the orchestrion code bundled as intended.
 *
 * In an unbundled (server-side runtime) app this eagerly loads only the small
 * subscriber/channel modules; the heavy code-transform dependencies stay lazy
 * inside `register()` and load only when injection actually runs.
 *
 * @experimental May change or be removed in any release.
 */
export function experimentalUseDiagnosticsChannelInjection(
  // Kept for backwards compatibility only; every field is deprecated and ignored.
  _options?: RegisterDiagnosticsChannelInjectionOptions,
): void {
  setDiagnosticsChannelInjectionLoader((): DiagnosticsChannelInjection => {
    // These channel integrations 1:1 replace the OTel integration of the
    // same name. Framework SDKs that own their own channel listener
    // (e.g. `@sentry/nestjs`'s `Nest`) are NOT here. They pick the
    // channel-vs-OTel path themselves at integration `setupOnce`, so
    // there's nothing for the central swap to do.
    const integrations = Object.values(channelIntegrations).map(createIntegration => createIntegration());
    const replacedOtelIntegrationNames = integrations.map(i => i.name);

    return {
      // ioredis and redis are wired separately (not in `channelIntegrations`): they need the node
      // redis cache `responseHook` and only partially replace the composite OTel `Redis` integration,
      // so they're kept OUT of `replacedOtelIntegrationNames` — `Redis` must stay (batch + >=5.11 native DC).
      integrations: [
        ...integrations,
        ioredisChannelIntegration({ responseHook: cacheResponseHook }),
        redisChannelIntegration({ responseHook: cacheResponseHook }),
      ],
      replacedOtelIntegrationNames,
      register: () => registerDiagnosticsChannelInjection(),
      detect: detectOrchestrionSetup,
    };
  });
}
